import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FilterIcon from '@mui/icons-material/Tune';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as R from 'ramda';

import { INITIAL_FILTERS_STATE, OUTAGE_OPTIONS, TIME_RANGE_OPTIONS } from './constants';

const buttonSectionStyles = {
  alignItems: 'center',
  backgroundColor: 'surface.white',
  borderTop: '1px solid',
  borderTopColor: 'graphic.light',
  bottom: 0,
  boxShadow: 0,
  display: 'flex',
  height: 68,
  justifyContent: 'center',
  position: 'sticky',
  px: 3,
  width: '100%',
  zIndex: 1,
};

const MobileFilters = ({ filtersState, isOpen, setFiltersState, setIsOpen }) => {
  const [mobileFiltersState, setMobileFiltersState] = useState(filtersState);
  const { outageCategory, timeRange } = mobileFiltersState;

  const handleOutageCategoryChange = R.compose(
    setMobileFiltersState,
    R.assoc('outageCategory'),
    R.ifElse(
      R.includes(R.__, outageCategory),
      R.without(R.__, outageCategory),
      R.append(R.__, outageCategory)
    )
  );

  const handleApplyClick = () => {
    setFiltersState(R.mergeLeft(mobileFiltersState));
    setIsOpen(false);
  };

  const handleCancelClick = () => {
    setMobileFiltersState(filtersState);
    setIsOpen(false);
  };

  return (
    <>
      <IconButton onClick={() => setIsOpen(true)}>
        <FilterIcon sx={{ color: 'secondary.main', transform: 'rotate( 90deg )' }} />
      </IconButton>
      <Drawer
        anchor="right"
        onClose={() => setIsOpen(false)}
        open={isOpen}
        slotProps={{
          paper: { sx: { width: '100%' } },
        }}
      >
        <Stack sx={{ backgroundColor: 'surface.white', height: '100%' }}>
          <Box display="flex" justifyContent="flex-end">
            <IconButton onClick={handleCancelClick}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>
          <Stack px={4} sx={{ flex: 1 }}>
            <Box alignItems="center" display="flex" justifyContent="space-between">
              <Typography variant="h6">Filter By</Typography>
              <Button
                onClick={() => setMobileFiltersState(R.mergeLeft(INITIAL_FILTERS_STATE))}
                px={0}
                variant="text"
              >
                Reset Filters
              </Button>
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormControl component="fieldset" sx={{ my: 3 }}>
                <FormLabel component="legend">
                  <Typography sx={{ color: 'text.primary' }} variant="subtitle1">
                    Time Range
                  </Typography>
                </FormLabel>
                <RadioGroup
                  onChange={event => {
                    setMobileFiltersState(
                      R.assoc(
                        'timeRange',
                        TIME_RANGE_OPTIONS.find(option => option.value === event.target.value)
                      )
                    );
                  }}
                  value={timeRange.value}
                >
                  {TIME_RANGE_OPTIONS.map((option, index) => (
                    <FormControlLabel
                      control={<Radio />}
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      label={option.label}
                      value={option.value}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              <Divider />
              <FormControl component="fieldset" sx={{ my: 2 }}>
                <FormLabel component="legend">
                  <Typography sx={{ color: 'text.primary' }} variant="subtitle1">
                    Outage Category
                  </Typography>
                </FormLabel>
                <FormGroup>
                  {OUTAGE_OPTIONS.map((option, index) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={R.includes(option, outageCategory)}
                          name={option}
                          onChange={() => handleOutageCategoryChange(option)}
                        />
                      }
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      label={option}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>
          </Stack>
          <Box sx={buttonSectionStyles}>
            <Box columnGap={3} display="grid" gridTemplateColumns="1fr 1fr" width="100%">
              <Button onClick={handleCancelClick} variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleApplyClick}>Apply</Button>
            </Box>
          </Box>
        </Stack>
      </Drawer>
    </>
  );
};

MobileFilters.propTypes = {
  filtersState: PropTypes.shape({}).isRequired,
  isOpen: PropTypes.bool.isRequired,
  setFiltersState: PropTypes.func.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};

export default MobileFilters;
